import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { AdminTitleHolder } from "../../../common/sharedComponents/styles/card.style";
import {
  Box,
  Card,
  Grid,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Button,
  MenuItem,
  FormControl,
  Select,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Checkbox,
  ListItemText,
  OutlinedInput,
  IconButton,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  CustomTableContainer,
  CustomTableHead,
  TableHeaderFont,
} from "../../../styles/tableContent.style";
import { Delete } from "@mui/icons-material";
import { DisplayText } from "../../bankDetails/components/bankAccountsContent";
import empty from "../../../assets/illo.png";
import { Super } from "../../vendorRegistration/components/steps/cardTrial";

import {
  assignCompany,
  getAssignCompanyList,
  getAssignUserList,
} from "../redux/businessUnitSlice";
import { useForm, Controller } from "react-hook-form";
import { history } from "../../../app/history";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const EmptyContainer = () => (
  <Grid
    container
    spacing={0}
    direction="column"
    alignItems="center"
    justifyContent="center"
  >
    <br />
    <img src={empty} alt="empty" />
    <DisplayText>No Users Added Yet</DisplayText>
    <br />
    <br />
  </Grid>
);

function BusinessUnitAssignList() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [localUsers, setLocalUsers] = useState([]);
  const [companyId, setCompanyId] = useState("");

  const { assignCompanyList, assignUserList } = useSelector(
    (state) => state.businessUnit,
  );
  const { loading } = useSelector((state) => state.businessUnit);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      userIds: [],
    },
  });

  const watchUserIds = watch("userIds");

  useEffect(() => {
    dispatch(getAssignCompanyList());
    dispatch(getAssignUserList());
  }, [dispatch]);

  const handleOpen = () => {
    setOpen(true);
    setValue("userIds", []);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const onAddUsers = (data) => {
    const newUsers = assignUserList.filter((u) => data.userIds.includes(u.id));
    const uniqueNewUsers = newUsers.filter(
      (nu) => !localUsers.some((lu) => lu.id === nu.id),
    );
    setLocalUsers([...localUsers, ...uniqueNewUsers]);
    handleClose();
  };

  const handleRemoveUser = (userId) => {
    setLocalUsers(localUsers.filter((u) => u.id !== userId));
  };

  const handleSave = () => {
    if (!companyId) {
      alert("Please select a company");
      return;
    }
    if (localUsers.length === 0) {
      alert("Please add at least one user");
      return;
    }

    const payload = {
      businessUnitId: id,
      companyId: companyId,
      userIds: localUsers.map((u) => u.id),
    };

    dispatch(assignCompany(payload)).then((res) => {
      if (res.payload?.success) {
        history.push("/app/business-unit");
      }
    });
  };

  const selectedCompany = assignCompanyList?.find((c) => c.id === companyId);

  const filteredUsersList =
    companyId && selectedCompany
      ? assignUserList?.filter(
          (u) => u.company === selectedCompany.company_name,
        )
      : [];

  return (
    <div>
      <AdminTitleHolder>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={6}>
            <FormControl variant="standard" fullWidth>
              <p>
                Company Name<Super>*</Super>
              </p>
              <Select
                value={companyId}
                onChange={(e) => {
                  setCompanyId(e.target.value);
                  setLocalUsers([]);
                }}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Select Company
                </MenuItem>
                {assignCompanyList?.map((comp) => (
                  <MenuItem key={comp.id} value={comp.id}>
                    {comp.company_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} textAlign="right"></Grid>
        </Grid>
      </AdminTitleHolder>

      <AdminTitleHolder sx={{ mt: 2 }}>
        <Grid container alignItems="center">
          <Grid item xs={6}>
            <b>Users</b>
          </Grid>
          <Grid item xs={6} textAlign="right">
            <Button
              variant="contained"
              onClick={handleOpen}
              disabled={!companyId}
            >
              Add Users
            </Button>
          </Grid>
        </Grid>
      </AdminTitleHolder>

      <CustomTableContainer component={Card}>
        <Table>
          <CustomTableHead>
            <TableRow>
              <TableCell>
                <TableHeaderFont>Name</TableHeaderFont>
              </TableCell>
              <TableCell>
                <TableHeaderFont>Email</TableHeaderFont>
              </TableCell>
              <TableCell>
                <TableHeaderFont>Action</TableHeaderFont>
              </TableCell>
            </TableRow>
          </CustomTableHead>
          <TableBody>
            {localUsers.length > 0 ? (
              localUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleRemoveUser(user.id)}>
                      <Delete sx={{ color: "red", fontSize: 18 }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3}>
                  <EmptyContainer />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CustomTableContainer>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
        <LoadingButton
          variant="contained"
          loading={loading}
          onClick={handleSave}
          sx={{ width: "150px" }}
        >
          Save
        </LoadingButton>
      </Box>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Add Users</DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit(onAddUsers)}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <p>
                    Select Users<Super>*</Super>
                  </p>
                  <Controller
                    name="userIds"
                    control={control}
                    rules={{ required: "Select at least one user" }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        multiple
                        displayEmpty
                        input={<OutlinedInput />}
                        renderValue={(selected) => {
                          if (selected.length === 0)
                            return <em>Select Users</em>;

                          const selectedNames = assignUserList
                            ?.filter((u) => selected.includes(u.id))
                            .map((u) => u.name)
                            .join(", ");
                          return selectedNames || "Selected";
                        }}
                        MenuProps={MenuProps}
                      >
                        <MenuItem disabled value="">
                          <em>Select Users</em>
                        </MenuItem>
                        {filteredUsersList?.map((user) => (
                          <MenuItem key={user.id} value={user.id}>
                            <Checkbox
                              checked={field.value.indexOf(user.id) > -1}
                            />
                            <ListItemText primary={user.name} />
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  <FormHelperText>{errors.userIds?.message}</FormHelperText>
                </FormControl>
              </Grid>
            </Grid>
            <DialogActions sx={{ mt: 2 }}>
              <Button onClick={handleClose} color="error" variant="contained">
                Cancel
              </Button>
              <Button type="submit" variant="contained">
                Add
              </Button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default BusinessUnitAssignList;
